import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Sword, Trophy, Calendar, BookOpen, Activity, Settings, LogOut, Menu, X, Download, Wallet, BarChart3 } from 'lucide-react';
import MobileMenu from './MobileMenu';
import NotificationPermissionModal from './NotificationPermissionModal';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { isAdminEquivalent, isFinanceRole, getRoleLabel } from '../utils/permissions';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { canInstall, promptInstall } = usePwaInstall();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/app/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Verificar se o usuário tem acesso ao painel admin
  const hasAdminAccess = isAdminEquivalent(user?.role);
  const hasRecruiterAccess = user?.role === 'RECRUITER';
  const hasFinanceAccess = isFinanceRole(user?.role);

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
    { name: 'Personagens', href: '/app/characters', icon: Sword },
    { name: 'Eventos', href: '/app/events', icon: Calendar },
    { name: 'Transparência', href: '/app/transparency', icon: BarChart3 },
    { name: 'Blog', href: '/app/blog', icon: BookOpen },
    { name: 'Ranking', href: '/app/ranking', icon: Trophy },
  ];

  const adminNavigation = [
    { name: 'Admin', href: '/app/admin', icon: Settings, adminOnly: true },
  ];

  const financeNavigation = hasFinanceAccess ? [
    { name: 'Caixa do Clube', href: '/app/admin/treasury', icon: Wallet },
  ] : [];

  // Adicionar navegação de recrutamento se o usuário for recrutador ou admin
  const recruiterNavigation = hasRecruiterAccess ? [
    { name: 'Recrutamentos', href: '/app/admin/enlistments', icon: Activity },
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-x-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none"></div>
      
      {/* Gradient blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-purple-500/10 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative z-50 bg-slate-900/80 backdrop-blur-xl shadow-2xl border-b border-white/10 sticky top-0 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/app/dashboard" className="flex items-center group">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative w-10 h-10 rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-all duration-300 overflow-hidden">
                    <img 
                      src="/logo.jfif" 
                      alt="Revolution Logo" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h1 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 group-hover:from-cyan-300 group-hover:to-blue-400 transition-all duration-300">
                    Revolution
                  </h1>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group relative px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                      isActive
                        ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/50'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity"></div>
                    )}
                  </Link>
                );
              })}
              
              {(hasAdminAccess || hasRecruiterAccess || hasFinanceAccess) && (
                <>
                  <div className="h-6 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent mx-2"></div>
                  {/* Menu de Recrutador */}
                  {recruiterNavigation.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group relative px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                          isActive
                            ? 'text-white bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-500/50'
                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                  {/* Menu Admin (apenas para ADMIN) */}
                  {hasAdminAccess && adminNavigation.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group relative px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                          isActive
                            ? 'text-white bg-gradient-to-r from-purple-500 to-pink-600 shadow-lg shadow-purple-500/50'
                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </>
              )}
            </nav>

            {/* User Menu & Mobile Toggle */}
            <div className="flex items-center space-x-4">
              {/* User Profile */}
              <div className="hidden sm:flex items-center space-x-3">
                <Link
                  to="/app/profile"
                  className="group flex items-center space-x-3 px-3 py-2 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity"></div>
                    <div className="relative w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center ring-2 ring-white/20 group-hover:ring-white/40 transition-all duration-300">
                      <span className="text-white text-sm font-bold">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <p className="font-semibold text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-400">
                      {getRoleLabel(user?.role)}
                    </p>
                  </div>
                </Link>
                
                {canInstall && (
                  <button
                    onClick={promptInstall}
                    className="p-2 rounded-xl text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all duration-300 group"
                    title="Instalar app"
                  >
                    <Download className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  className="p-2 rounded-xl text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-300 group"
                  title="Sair"
                >
                  <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          user={user}
          navigation={navigation}
          recruiterNavigation={recruiterNavigation}
          adminNavigation={hasAdminAccess ? adminNavigation : []}
          financeNavigation={hasFinanceAccess ? financeNavigation : []}
          onLogout={handleLogout}
        />
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="min-h-[calc(100vh-8rem)]">
          {children}
        </div>
      </main>

      <NotificationPermissionModal />
    </div>
  );
};

export default Layout;